import { lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import PageNavbar from '../../components/feature/PageNavbar';
import Footer from '../home/components/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import PortfolioHero from './components/PortfolioHero';
import {
  useSEO,
  generateWebPageSchema,
  generateBreadcrumbSchema,
} from '../../utils/seo';

const PortfolioGrid = lazy(() => import('./components/PortfolioGrid'));

const SectionLoader = () => (
  <div className="py-16 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function PortfolioPage() {
  useSEO({
    title: 'Portfolio & Referenzen - Webdesign Titisee-Neustadt | BR Webdesign',
    description:
      'Webdesign-Referenzen aus Titisee-Neustadt: Webseiten für Handwerk, Gastronomie & Dienstleister im Schwarzwald. Echte Ergebnisse, echte Kunden.',
    keywords:
      'Webdesign Referenzen Titisee-Neustadt, Portfolio Webseiten Schwarzwald, Webdesign Beispiele 79822',
    canonical: '/portfolio',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Portfolio & Referenzen – BR Webdesign & AI Solutions',
          'Referenzprojekte und Beispielwebseiten von Benjamin Reick aus Titisee-Neustadt',
          '/portfolio'
        ),
        generateBreadcrumbSchema([
          { name: 'Startseite', url: '/' },
          { name: 'Portfolio', url: '/portfolio' },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />

      <PortfolioHero />

      <Suspense fallback={<SectionLoader />}>
        <PortfolioGrid />
      </Suspense>

      {/* Process Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block bg-brand-green/10 text-brand-green text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wider">
              Mein Prozess
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-caprasimo text-brand-black mb-4">
              So entsteht Ihr Projekt
            </h2>
            <p className="text-brand-grey text-lg max-w-2xl mx-auto">
              Von der ersten Idee bis zur fertigen Webseite – in maximal einer Woche
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', icon: 'ri-chat-smile-2-line', title: 'Erstgespräch', desc: 'Kostenlos & unverbindlich. Wir besprechen Ihre Ziele, Wünsche und Anforderungen.' },
              { step: '02', icon: 'ri-palette-line', title: 'Design & Konzept', desc: 'Ich erstelle ein maßgeschneidertes Design, das zu Ihrer Marke und Zielgruppe passt.' },
              { step: '03', icon: 'ri-code-s-slash-line', title: 'Entwicklung', desc: 'Schnelle, saubere Umsetzung mit modernsten Technologien und SEO-Optimierung.' },
              { step: '04', icon: 'ri-rocket-2-line', title: 'Launch & Support', desc: 'Ihre Webseite geht live – inklusive 2 kostenloser Änderungen und persönlichem Support.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative bg-brand-beige-light rounded-2xl p-6 border border-brand-beige hover:border-brand-green hover:shadow-xl transition-all group"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 flex items-center justify-center bg-brand-green text-white text-xs font-bold rounded-full">
                  {item.step}
                </div>
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mb-4 group-hover:bg-brand-green group-hover:scale-110 transition-all">
                  <i className={`${item.icon} text-2xl text-brand-green group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-base font-caprasimo text-brand-black mb-2 group-hover:text-brand-green transition-colors">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-brand-beige-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-caprasimo text-brand-black mb-4 sm:mb-6">
              Ihr Projekt als nächstes?
            </h2>
            <p className="text-lg sm:text-xl text-brand-grey mb-8 sm:mb-10 max-w-2xl mx-auto">
              Lassen Sie uns gemeinsam Ihre Webseite in eine leistungsstarke Verkaufsmaschine verwandeln – in maximal einer Woche.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <a href="/contact" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-white px-8 sm:px-10 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center gap-3 min-h-[44px]">
                <i className="ri-send-plane-line text-xl"></i>
                Kostenloses Erstgespräch
              </a>
              <a href="/pricing" className="w-full sm:w-auto bg-white hover:bg-brand-beige text-brand-black px-8 sm:px-10 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all cursor-pointer whitespace-nowrap border border-brand-beige flex items-center justify-center gap-3 min-h-[44px]">
                <i className="ri-price-tag-3-line"></i>
                Preise ansehen
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
}
